import { Injectable } from '@angular/core';
import {
  RaffleCategory,
  RaffleSimple,
  SettingsLogo,
  SettingsModel,
  SettingsOrderDiscount
} from '@core/models';
import { RaffleService } from '@core/services';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import {
  GetCategories,
  SetActiveProperty,
  SetOrderDiscount,
  SetSettingsData,
  UpdateInstallPrompt
} from '@store/settings/settings.actions';
import { tap } from 'rxjs/operators';

export class SettingsStateModel {
  settings: SettingsModel | null = null;
  isInitiated = false;
  installPrompt = true;
  activeProperty: RaffleSimple;
  logo: SettingsLogo;
  orderDiscount: SettingsOrderDiscount;
  categories: RaffleCategory[];
}

@State<SettingsStateModel>({
  name: 'settings',
  defaults: new SettingsStateModel()
})
@Injectable()
export class SettingsState implements NgxsOnInit {
  @Selector()
  static settings(state: SettingsStateModel): SettingsModel | null {
    return state.settings;
  }

  @Selector()
  static paymentsDisabledMessageHomepage(state: SettingsStateModel): string {
    return state.settings
      ? state.settings.payments_disabled_message_homepage
      : '';
  }

  @Selector()
  static totalPrizesValue(state: SettingsStateModel): string | null {
    return state.settings ? state.settings.total_prizes_value : '';
  }

  @Selector()
  static totalWinners(state: SettingsStateModel): string | null {
    return state.settings ? state.settings.total_winners : '';
  }

  @Selector()
  static getAppBanner(state: SettingsStateModel): string | null {
    return state.settings ? state.settings.app_homepage_banner : null;
  }

  @Selector()
  static state(state: SettingsStateModel): SettingsStateModel {
    return state;
  }

  @Selector()
  static installPrompt(state: SettingsStateModel): boolean {
    return state.installPrompt;
  }

  @Selector()
  static activeProperty(state: SettingsStateModel): RaffleSimple {
    return state.activeProperty;
  }

  @Selector()
  static settingsLogo(state: SettingsStateModel): SettingsLogo {
    return state.logo;
  }

  @Selector()
  static orderAmountThreshold(state: SettingsStateModel): number {
    return state.settings.order_amount_threshold || 250;
  }

  @Selector()
  static orderDiscount(state: SettingsStateModel): SettingsOrderDiscount {
    return state.orderDiscount;
  }

  @Selector()
  static categories(state: SettingsStateModel): RaffleCategory[] {
    return state.categories;
  }

  constructor(private raffleService: RaffleService) {}

  ngxsOnInit(ctx: StateContext<SettingsStateModel>): void {
    ctx.patchState({
      isInitiated: true
    });
  }

  @Action(SetSettingsData)
  setSettingsData(
    ctx: StateContext<SettingsStateModel>,
    action: SetSettingsData
  ): void {
    ctx.patchState({
      settings: !action.settings ? null : action.settings,
      logo: {
        url: action.settings?.logo,
        logoResolved: true
      }
    });
  }

  @Action(UpdateInstallPrompt)
  updateInstallPrompt(
    ctx: StateContext<SettingsStateModel>,
    action: UpdateInstallPrompt
  ): void {
    ctx.patchState({
      installPrompt: action.isVisible
    });
  }

  @Action(SetActiveProperty)
  setActiveProperty(ctx: StateContext<SettingsStateModel>) {
    return this.raffleService
      .getRaffleDraws('GBP', ['property'], 48, false, true, 0, 'date')
      .pipe(
        tap({
          next: data => {
            const now = new Date();
            const nowUK = new Date(
              now.toLocaleString('en-US', { timeZone: 'Europe/London' })
            ).getTime();

            const activeProperty =
              data.find(item => {
                const start = new Date(item.start_at).getTime();
                const end = new Date(item.end_at).getTime();
                return nowUK >= start && nowUK <= end;
              }) ?? ({} as RaffleSimple);

            ctx.patchState({
              activeProperty
            });
          },
          error: () =>
            ctx.patchState({
              activeProperty: {} as RaffleSimple
            })
        })
      );
  }

  @Action(SetOrderDiscount)
  setOrderDiscount(
    ctx: StateContext<SettingsStateModel>,
    action: SetOrderDiscount
  ): void {
    ctx.patchState({
      orderDiscount: action.orderDiscount
    });
  }

  @Action(GetCategories)
  getCategories(ctx: StateContext<SettingsStateModel>) {
    return this.raffleService.getCategories().pipe(
      tap({
        next: categories => ctx.patchState({ categories }),
        error: () => ctx.patchState({ categories: [] })
      })
    );
  }
}
